import { ConfigProvider, theme as antdTheme } from 'antd';
import {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DEFAULT_DESIGN_PRESET,
  DESIGN_PRESET_STORAGE_KEY,
  getDesignPreset,
  designPresetList,
} from './designPresets';
import {
  allVisibilityKeys,
  buildDesignCustomization,
  defaultDesignCustomization,
  DESIGN_CUSTOMIZATION_STORAGE_KEY,
  DESIGN_PREFERENCE_KEYS,
  designCustomizationOptions,
  normalizeDesignCustomization,
  pickDesignCustomization,
  pickVisibilityFromCustomization,
  tenantVisibilityKeys,
  userVisibilityKeys,
} from './designCustomization';
import {
  getNavigationLayout,
  navigationLayoutList,
} from '../../shared/ui/layout/navigationLayouts';
import {
  buildCustomWorkspaceMode,
  composeWorkspaceModes,
  getStoredWorkspaceModes,
  getWorkspaceMode,
  persistWorkspaceModes,
  resolveWorkspaceMode,
  workspaceModeList,
} from './workspaceModes';
import { useAuthSession } from './AuthSessionProvider';
import { ENABLE_AUTH } from '../../shared/utils/constants';
import {
  fetchDesignPreferences,
  fetchVisibilityPreferences,
  updateDesignPreferences,
  updateVisibilityPreferences,
} from '../../features/settings/api/queries';
import { hasPermission } from '../../shared/auth/permissions';

export const ASSIST_PANEL_STORAGE_KEY = 'bes-ui-show-assist-panel';
const VISIBILITY_PERSIST_DELAY_MS = 500;
const DESIGN_PERSIST_DELAY_MS = 500;
const MOBILE_HELPER_TEXT_MEDIA_QUERY = '(max-width: 768px)';

const defaultDesignPreferenceState = Object.freeze({
  presetId: DEFAULT_DESIGN_PRESET,
  showAssistPanel: false,
  ...pickDesignCustomization(defaultDesignCustomization),
});

function canManageTenantVisibility(role, permissions) {
  return role === 'owner' || hasPermission(permissions, 'settings:manage');
}

function canManageTenantDesign(role, permissions) {
  return role === 'owner' || hasPermission(permissions, 'settings:manage');
}

function normalizePresetId(value) {
  return getDesignPreset(value).id;
}

function normalizeDesignPreferencePatch(input = {}) {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return {};
  }

  const normalizedCustomization = normalizeDesignCustomization(input);
  const patch = {};

  if (Object.prototype.hasOwnProperty.call(input, 'presetId')) {
    patch.presetId = normalizePresetId(input.presetId);
  }

  if (Object.prototype.hasOwnProperty.call(input, 'showAssistPanel')) {
    if (typeof input.showAssistPanel === 'boolean') {
      patch.showAssistPanel = input.showAssistPanel;
    }
  }

  DESIGN_PREFERENCE_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(input, key)) {
      patch[key] = normalizedCustomization[key];
    }
  });

  return patch;
}

function mergeDesignPreferencePatches(...patches) {
  return patches.reduce((accumulator, patch) => {
    if (!patch || typeof patch !== 'object') {
      return accumulator;
    }

    return {
      ...accumulator,
      ...normalizeDesignPreferencePatch(patch),
    };
  }, {});
}

function resolveDesignPreferenceState(...patches) {
  const mergedPatch = mergeDesignPreferencePatches(...patches);

  return {
    ...defaultDesignPreferenceState,
    ...mergedPatch,
  };
}

function buildVisibilityPayload(customization, role, permissions) {
  const userOverrides = pickVisibilityFromCustomization(customization, userVisibilityKeys);

  if (!canManageTenantVisibility(role, permissions)) {
    return { userOverrides };
  }

  return {
    tenantDefaults: pickVisibilityFromCustomization(customization, tenantVisibilityKeys),
    userOverrides,
  };
}

function getInitialPreset() {
  if (typeof window === 'undefined') {
    return DEFAULT_DESIGN_PRESET;
  }
  return window.localStorage.getItem(DESIGN_PRESET_STORAGE_KEY) || DEFAULT_DESIGN_PRESET;
}

function getInitialAssistPanelPreference() {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(ASSIST_PANEL_STORAGE_KEY) === 'true';
}

function getInitialDesignCustomization() {
  if (typeof window === 'undefined') {
    return defaultDesignCustomization;
  }

  try {
    const rawValue = window.localStorage.getItem(DESIGN_CUSTOMIZATION_STORAGE_KEY);
    if (!rawValue) {
      return defaultDesignCustomization;
    }

    return normalizeDesignCustomization(JSON.parse(rawValue));
  } catch (_error) {
    return defaultDesignCustomization;
  }
}

function getInitialUserDesignPreferences() {
  return normalizeDesignPreferencePatch({
    presetId: getInitialPreset(),
    showAssistPanel: getInitialAssistPanelPreference(),
    ...getInitialDesignCustomization(),
  });
}

function getInitialMobileHelperTextViewport() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }

  return Boolean(window.matchMedia(MOBILE_HELPER_TEXT_MEDIA_QUERY)?.matches);
}

function getInitialCustomWorkspaceModes() {
  if (typeof window === 'undefined') {
    return [];
  }

  return getStoredWorkspaceModes(window.localStorage);
}

function shouldHydrateWorkspacePreferenceRoutes(pathname = '') {
  const normalizedPath = String(pathname || '').trim();

  if (!normalizedPath || normalizedPath === '/') {
    return false;
  }

  return !(
    normalizedPath === '/landing' ||
    normalizedPath === '/login' ||
    normalizedPath.startsWith('/admin') ||
    normalizedPath.startsWith('/customer-portal') ||
    normalizedPath.startsWith('/accept-invitation') ||
    normalizedPath.startsWith('/auth/clerk/sign-in')
  );
}

const DesignSystemContext = createContext({
  presetId: DEFAULT_DESIGN_PRESET,
  activePreset: getDesignPreset(DEFAULT_DESIGN_PRESET),
  presets: designPresetList,
  activeNavigationLayout: getNavigationLayout(defaultDesignCustomization.navigationLayout),
  navigationLayouts: navigationLayoutList,
  activeWorkspaceMode: resolveWorkspaceMode(
    DEFAULT_DESIGN_PRESET,
    defaultDesignCustomization,
    false
  ),
  workspaceModes: workspaceModeList,
  uiCustomization: defaultDesignCustomization,
  customizationOptions: designCustomizationOptions,
  showHelperText: false,
  showAssistPanel: false,
  designScopes: {
    user: {
      presetId: DEFAULT_DESIGN_PRESET,
      activePreset: getDesignPreset(DEFAULT_DESIGN_PRESET),
      activeNavigationLayout: getNavigationLayout(defaultDesignCustomization.navigationLayout),
      activeWorkspaceMode: resolveWorkspaceMode(
        DEFAULT_DESIGN_PRESET,
        defaultDesignCustomization,
        false
      ),
      uiCustomization: defaultDesignCustomization,
      showAssistPanel: false,
    },
    tenant: {
      presetId: DEFAULT_DESIGN_PRESET,
      activePreset: getDesignPreset(DEFAULT_DESIGN_PRESET),
      activeNavigationLayout: getNavigationLayout(defaultDesignCustomization.navigationLayout),
      activeWorkspaceMode: resolveWorkspaceMode(
        DEFAULT_DESIGN_PRESET,
        defaultDesignCustomization,
        false
      ),
      uiCustomization: defaultDesignCustomization,
      showAssistPanel: false,
    },
  },
  canManageTenantDesignDefaults: false,
  applyWorkspaceMode: () => {},
  applyWorkspaceModeForScope: () => {},
  setPresetId: () => {},
  setPresetIdForScope: () => {},
  setUiCustomization: () => {},
  setUiCustomizationForScope: () => {},
  resetUiCustomization: () => {},
  resetUiCustomizationForScope: () => {},
  setShowAssistPanel: () => {},
  setShowAssistPanelForScope: () => {},
  saveWorkspaceMode: () => null,
  removeWorkspaceMode: () => {},
});

export function useDesignSystem() {
  return useContext(DesignSystemContext);
}

export default function ThemeProvider({ children }) {
  const { session, isReady: isAuthReady } = useAuthSession();
  const [routePathname, setRoutePathname] = useState(() =>
    typeof window === 'undefined' ? '' : window.location.pathname || ''
  );
  const [uiCustomizationState, setUiCustomizationState] = useState(getInitialDesignCustomization);
  const [tenantDesignPreferences, setTenantDesignPreferencesState] = useState({});
  const [userDesignPreferences, setUserDesignPreferencesState] = useState(
    getInitialUserDesignPreferences
  );
  const [customWorkspaceModes, setCustomWorkspaceModes] = useState(getInitialCustomWorkspaceModes);
  const [isMobileHelperTextViewport, setIsMobileHelperTextViewport] = useState(
    getInitialMobileHelperTextViewport
  );
  const visibilityHydratedRef = useRef(!ENABLE_AUTH);
  const designHydratedRef = useRef(!ENABLE_AUTH);
  const lastSyncedVisibilityRef = useRef('');
  const lastSyncedUserDesignRef = useRef('');
  const lastSyncedTenantDesignRef = useRef('');
  const visibilitySyncTimerRef = useRef(null);
  const userDesignSyncTimerRef = useRef(null);
  const tenantDesignSyncTimerRef = useRef(null);
  const appliedCssVarKeysRef = useRef(new Set());
  const hasServerPreferenceContext = Boolean(
    ENABLE_AUTH &&
      shouldHydrateWorkspacePreferenceRoutes(routePathname) &&
      session?.tenantId &&
      session?.user?.id &&
      session?.accessToken
  );
  const canManageTenantDesignDefaults = canManageTenantDesign(
    session?.role,
    session?.permissions || []
  );

  const workspaceModes = useMemo(
    () => composeWorkspaceModes(customWorkspaceModes),
    [customWorkspaceModes]
  );

  const effectiveDesignPreferences = useMemo(
    () => resolveDesignPreferenceState(tenantDesignPreferences, userDesignPreferences),
    [tenantDesignPreferences, userDesignPreferences]
  );
  const effectiveTenantDesignPreferences = useMemo(
    () => resolveDesignPreferenceState(tenantDesignPreferences),
    [tenantDesignPreferences]
  );

  const uiCustomization = useMemo(
    () =>
      normalizeDesignCustomization({
        ...uiCustomizationState,
        ...pickDesignCustomization(effectiveDesignPreferences),
      }),
    [effectiveDesignPreferences, uiCustomizationState]
  );
  const tenantUiCustomization = useMemo(
    () =>
      normalizeDesignCustomization({
        ...defaultDesignCustomization,
        ...pickDesignCustomization(effectiveTenantDesignPreferences),
      }),
    [effectiveTenantDesignPreferences]
  );

  const presetId = effectiveDesignPreferences.presetId;
  const showAssistPanel = effectiveDesignPreferences.showAssistPanel;
  const activePreset = useMemo(() => getDesignPreset(presetId), [presetId]);
  const showHelperText = useMemo(
    () => Boolean(uiCustomization.showHelperText) && !isMobileHelperTextViewport,
    [isMobileHelperTextViewport, uiCustomization.showHelperText]
  );
  const activeNavigationLayout = useMemo(
    () => getNavigationLayout(uiCustomization.navigationLayout),
    [uiCustomization.navigationLayout]
  );
  const activeWorkspaceMode = useMemo(
    () => resolveWorkspaceMode(activePreset.id, uiCustomization, showAssistPanel, workspaceModes),
    [activePreset.id, showAssistPanel, uiCustomization, workspaceModes]
  );

  const tenantPresetId = effectiveTenantDesignPreferences.presetId;
  const tenantShowAssistPanel = effectiveTenantDesignPreferences.showAssistPanel;
  const tenantActivePreset = useMemo(() => getDesignPreset(tenantPresetId), [tenantPresetId]);
  const tenantActiveNavigationLayout = useMemo(
    () => getNavigationLayout(tenantUiCustomization.navigationLayout),
    [tenantUiCustomization.navigationLayout]
  );
  const tenantActiveWorkspaceMode = useMemo(
    () =>
      resolveWorkspaceMode(
        tenantActivePreset.id,
        tenantUiCustomization,
        tenantShowAssistPanel,
        workspaceModes
      ),
    [tenantActivePreset.id, tenantShowAssistPanel, tenantUiCustomization, workspaceModes]
  );

  const customizationTheme = useMemo(
    () => buildDesignCustomization(activePreset, uiCustomization),
    [activePreset, uiCustomization]
  );
  const resolvedCssVars = useMemo(
    () => ({
      ...activePreset.cssVars,
      ...customizationTheme.cssVars,
    }),
    [activePreset.cssVars, customizationTheme.cssVars]
  );
  const resolvedAntd = useMemo(
    () => ({
      ...activePreset.antd,
      ...customizationTheme.antd,
    }),
    [activePreset.antd, customizationTheme.antd]
  );

  const setPresetIdForScope = useCallback((scope, nextPresetId) => {
    const patch = normalizeDesignPreferencePatch({ presetId: nextPresetId });

    if (scope === 'tenant') {
      setTenantDesignPreferencesState((currentValue) =>
        mergeDesignPreferencePatches(currentValue, patch)
      );
      return;
    }

    setUserDesignPreferencesState((currentValue) =>
      mergeDesignPreferencePatches(currentValue, patch)
    );
  }, []);

  const setShowAssistPanelForScope = useCallback((scope, nextValue) => {
    const patch = normalizeDesignPreferencePatch({ showAssistPanel: nextValue });

    if (scope === 'tenant') {
      setTenantDesignPreferencesState((currentValue) =>
        mergeDesignPreferencePatches(currentValue, patch)
      );
      return;
    }

    setUserDesignPreferencesState((currentValue) =>
      mergeDesignPreferencePatches(currentValue, patch)
    );
  }, []);

  const setUiCustomizationForScope = useCallback(
    (scope, nextValue) => {
      const currentScopedCustomization =
        scope === 'tenant' ? tenantUiCustomization : uiCustomization;
      const resolvedUpdate =
        typeof nextValue === 'function' ? nextValue(currentScopedCustomization) : nextValue;
      const normalizedUpdate = normalizeDesignCustomization({
        ...currentScopedCustomization,
        ...(resolvedUpdate || {}),
      });
      const designPatch = normalizeDesignPreferencePatch(resolvedUpdate || {});

      if (scope === 'tenant') {
        if (Object.keys(designPatch).length > 0) {
          setTenantDesignPreferencesState((currentValue) =>
            mergeDesignPreferencePatches(currentValue, designPatch)
          );
        }
        return;
      }

      const localPatch = Object.entries(resolvedUpdate || {}).reduce((accumulator, [key, value]) => {
        if (!DESIGN_PREFERENCE_KEYS.includes(key)) {
          accumulator[key] = value;
        }
        return accumulator;
      }, {});

      if (Object.keys(designPatch).length > 0) {
        setUserDesignPreferencesState((currentValue) =>
          mergeDesignPreferencePatches(currentValue, designPatch)
        );
      }

      if (Object.keys(localPatch).length > 0) {
        setUiCustomizationState((currentValue) =>
          normalizeDesignCustomization({
            ...currentValue,
            ...localPatch,
          })
        );
        return;
      }

      if (!Object.keys(designPatch).length && normalizedUpdate !== currentScopedCustomization) {
        setUiCustomizationState((currentValue) =>
          normalizeDesignCustomization({
            ...currentValue,
          })
        );
      }
    },
    [tenantUiCustomization, uiCustomization]
  );

  const resetUiCustomizationForScope = useCallback((scope) => {
    if (scope === 'tenant') {
      setTenantDesignPreferencesState({});
      return;
    }

    setUserDesignPreferencesState({});
    setUiCustomizationState(defaultDesignCustomization);
  }, []);

  const applyWorkspaceModeForScope = useCallback(
    (scope, modeId) => {
      const nextMode = getWorkspaceMode(modeId, workspaceModes);
      const nextPatch = normalizeDesignPreferencePatch({
        presetId: nextMode.presetId,
        showAssistPanel: nextMode.showAssistPanel,
        ...pickDesignCustomization(nextMode.customization),
      });

      if (scope === 'tenant') {
        setTenantDesignPreferencesState(nextPatch);
        return;
      }

      setUserDesignPreferencesState(nextPatch);
      setUiCustomizationState((currentValue) =>
        normalizeDesignCustomization({
          ...currentValue,
          ...nextMode.customization,
        })
      );
    },
    [workspaceModes]
  );

  const setPresetId = useCallback((nextPresetId) => {
    setPresetIdForScope('user', nextPresetId);
  }, [setPresetIdForScope]);

  const setShowAssistPanel = useCallback((nextValue) => {
    setShowAssistPanelForScope('user', nextValue);
  }, [setShowAssistPanelForScope]);

  const setUiCustomization = useCallback(
    (nextValue) => {
      setUiCustomizationForScope('user', nextValue);
    },
    [setUiCustomizationForScope]
  );

  const resetUiCustomization = useCallback(() => {
    resetUiCustomizationForScope('user');
  }, [resetUiCustomizationForScope]);

  const applyWorkspaceMode = useCallback(
    (modeId) => {
      applyWorkspaceModeForScope('user', modeId);
    },
    [applyWorkspaceModeForScope]
  );

  const saveWorkspaceMode = useCallback(
    ({ id = undefined, label, description = '', tags = ['Custom', 'Saved'] }) => {
      const nextMode = buildCustomWorkspaceMode({
        id,
        label,
        description,
        tags,
        presetId: activePreset.id,
        showAssistPanel,
        customization: uiCustomization,
      });

      setCustomWorkspaceModes((currentValue) => {
        const nextCustomModes = currentValue.filter((mode) => mode.id !== nextMode.id);
        return [...nextCustomModes, nextMode];
      });

      return nextMode;
    },
    [activePreset.id, showAssistPanel, uiCustomization]
  );

  const removeWorkspaceMode = useCallback((modeId) => {
    setCustomWorkspaceModes((currentValue) =>
      currentValue.filter((mode) => mode.id !== modeId)
    );
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQueryList = window.matchMedia(MOBILE_HELPER_TEXT_MEDIA_QUERY);
    if (!mediaQueryList) {
      return undefined;
    }

    const syncViewport = (event) => {
      setIsMobileHelperTextViewport(Boolean(event.matches));
    };

    setIsMobileHelperTextViewport(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', syncViewport);
      return () => mediaQueryList.removeEventListener('change', syncViewport);
    }

    mediaQueryList.addListener(syncViewport);
    return () => mediaQueryList.removeListener(syncViewport);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncPathname = () => {
      setRoutePathname(window.location.pathname || '');
    };

    syncPathname();
    window.addEventListener('popstate', syncPathname);
    window.addEventListener('hashchange', syncPathname);

    return () => {
      window.removeEventListener('popstate', syncPathname);
      window.removeEventListener('hashchange', syncPathname);
    };
  }, []);

  useEffect(() => {
    if (visibilitySyncTimerRef.current) {
      window.clearTimeout(visibilitySyncTimerRef.current);
      visibilitySyncTimerRef.current = null;
    }
    if (userDesignSyncTimerRef.current) {
      window.clearTimeout(userDesignSyncTimerRef.current);
      userDesignSyncTimerRef.current = null;
    }
    if (tenantDesignSyncTimerRef.current) {
      window.clearTimeout(tenantDesignSyncTimerRef.current);
      tenantDesignSyncTimerRef.current = null;
    }

    visibilityHydratedRef.current = !hasServerPreferenceContext;
    designHydratedRef.current = !hasServerPreferenceContext;
    lastSyncedVisibilityRef.current = '';
    lastSyncedUserDesignRef.current = '';
    lastSyncedTenantDesignRef.current = '';
  }, [hasServerPreferenceContext, routePathname, session?.tenantId, session?.user?.id]);

  useEffect(() => {
    if (hasServerPreferenceContext) {
      return;
    }

    if (!ENABLE_AUTH) {
      designHydratedRef.current = true;
      visibilityHydratedRef.current = true;
      return;
    }

    if (isAuthReady) {
      setTenantDesignPreferencesState({});
      setUserDesignPreferencesState({});
      setUiCustomizationState(defaultDesignCustomization);
      designHydratedRef.current = true;
      visibilityHydratedRef.current = true;
    }
  }, [hasServerPreferenceContext, isAuthReady]);

  useEffect(() => {
    if (!hasServerPreferenceContext) {
      return undefined;
    }

    let active = true;
    const role = session?.role;
    const permissions = session?.permissions || [];

    Promise.all([fetchDesignPreferences(), fetchVisibilityPreferences()])
      .then(([designData, visibilityData]) => {
        if (!active) {
          return;
        }

        const nextTenantDesignPreferences = normalizeDesignPreferencePatch(
          designData?.tenantDefaults || {}
        );
        const nextUserDesignPreferences = normalizeDesignPreferencePatch(
          designData?.userOverrides || {}
        );

        setTenantDesignPreferencesState(nextTenantDesignPreferences);
        setUserDesignPreferencesState(nextUserDesignPreferences);
        lastSyncedTenantDesignRef.current = JSON.stringify(nextTenantDesignPreferences);
        lastSyncedUserDesignRef.current = JSON.stringify(nextUserDesignPreferences);

        setUiCustomizationState((currentValue) => {
          const nextValue = normalizeDesignCustomization({
            ...currentValue,
            ...pickVisibilityFromCustomization(visibilityData?.effective || {}, allVisibilityKeys),
          });
          lastSyncedVisibilityRef.current = JSON.stringify(
            buildVisibilityPayload(nextValue, role, permissions)
          );
          return nextValue;
        });
      })
      .catch(() => {
        if (!active) {
          return;
        }
      })
      .finally(() => {
        if (active) {
          designHydratedRef.current = true;
          visibilityHydratedRef.current = true;
        }
      });

    return () => {
      active = false;
    };
  }, [hasServerPreferenceContext, session]);

  useEffect(() => {
    if (!hasServerPreferenceContext || !designHydratedRef.current) {
      return undefined;
    }

    const payload = normalizeDesignPreferencePatch(userDesignPreferences);
    const payloadHash = JSON.stringify(payload);

    if (payloadHash === lastSyncedUserDesignRef.current) {
      return undefined;
    }

    if (userDesignSyncTimerRef.current) {
      window.clearTimeout(userDesignSyncTimerRef.current);
      userDesignSyncTimerRef.current = null;
    }

    userDesignSyncTimerRef.current = window.setTimeout(() => {
      updateDesignPreferences({ userOverrides: payload })
        .then((data) => {
          const nextUserDesignPreferences = normalizeDesignPreferencePatch(data?.userOverrides || {});
          setUserDesignPreferencesState(nextUserDesignPreferences);
          lastSyncedUserDesignRef.current = JSON.stringify(nextUserDesignPreferences);
        })
        .catch(() => null)
        .finally(() => {
          userDesignSyncTimerRef.current = null;
        });
    }, DESIGN_PERSIST_DELAY_MS);

    return () => {
      if (userDesignSyncTimerRef.current) {
        window.clearTimeout(userDesignSyncTimerRef.current);
        userDesignSyncTimerRef.current = null;
      }
    };
  }, [hasServerPreferenceContext, userDesignPreferences]);

  useEffect(() => {
    if (
      !hasServerPreferenceContext ||
      !designHydratedRef.current ||
      !canManageTenantDesignDefaults
    ) {
      return undefined;
    }

    const payload = normalizeDesignPreferencePatch(tenantDesignPreferences);
    const payloadHash = JSON.stringify(payload);

    if (payloadHash === lastSyncedTenantDesignRef.current) {
      return undefined;
    }

    if (tenantDesignSyncTimerRef.current) {
      window.clearTimeout(tenantDesignSyncTimerRef.current);
      tenantDesignSyncTimerRef.current = null;
    }

    tenantDesignSyncTimerRef.current = window.setTimeout(() => {
      updateDesignPreferences({ tenantDefaults: payload })
        .then((data) => {
          const nextTenantDesignPreferences = normalizeDesignPreferencePatch(
            data?.tenantDefaults || {}
          );
          setTenantDesignPreferencesState(nextTenantDesignPreferences);
          lastSyncedTenantDesignRef.current = JSON.stringify(nextTenantDesignPreferences);
        })
        .catch(() => null)
        .finally(() => {
          tenantDesignSyncTimerRef.current = null;
        });
    }, DESIGN_PERSIST_DELAY_MS);

    return () => {
      if (tenantDesignSyncTimerRef.current) {
        window.clearTimeout(tenantDesignSyncTimerRef.current);
        tenantDesignSyncTimerRef.current = null;
      }
    };
  }, [
    canManageTenantDesignDefaults,
    hasServerPreferenceContext,
    tenantDesignPreferences,
  ]);

  useEffect(() => {
    if (!hasServerPreferenceContext || !visibilityHydratedRef.current) {
      return undefined;
    }

    const role = session?.role;
    const permissions = session?.permissions || [];
    const payload = buildVisibilityPayload(uiCustomization, role, permissions);
    const payloadHash = JSON.stringify(payload);

    if (payloadHash === lastSyncedVisibilityRef.current) {
      return undefined;
    }

    if (visibilitySyncTimerRef.current) {
      window.clearTimeout(visibilitySyncTimerRef.current);
      visibilitySyncTimerRef.current = null;
    }

    visibilitySyncTimerRef.current = window.setTimeout(() => {
      updateVisibilityPreferences(payload)
        .then((data) => {
          setUiCustomizationState((currentValue) => {
            const nextValue = normalizeDesignCustomization({
              ...currentValue,
              ...pickVisibilityFromCustomization(data?.effective || {}, allVisibilityKeys),
            });
            lastSyncedVisibilityRef.current = JSON.stringify(
              buildVisibilityPayload(nextValue, role, permissions)
            );
            return nextValue;
          });
        })
        .catch(() => null)
        .finally(() => {
          visibilitySyncTimerRef.current = null;
        });
    }, VISIBILITY_PERSIST_DELAY_MS);

    return () => {
      if (visibilitySyncTimerRef.current) {
        window.clearTimeout(visibilitySyncTimerRef.current);
        visibilitySyncTimerRef.current = null;
      }
    };
  }, [hasServerPreferenceContext, session, uiCustomization]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    window.localStorage.setItem(DESIGN_PRESET_STORAGE_KEY, activePreset.id);

    const root = document.documentElement;
    const nextCssKeys = new Set(Object.keys(resolvedCssVars));

    appliedCssVarKeysRef.current.forEach((cssVarName) => {
      if (!nextCssKeys.has(cssVarName)) {
        root.style.removeProperty(cssVarName);
      }
    });

    Object.entries(resolvedCssVars).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
    appliedCssVarKeysRef.current = nextCssKeys;
    root.dataset.designPreset = activePreset.id;
    root.dataset.navigationLayout = activeNavigationLayout.id;
    root.style.colorScheme = activePreset.mode === 'dark' ? 'dark' : 'light';
  }, [activeNavigationLayout.id, activePreset, resolvedCssVars]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(ASSIST_PANEL_STORAGE_KEY, String(showAssistPanel));
  }, [showAssistPanel]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(
      DESIGN_CUSTOMIZATION_STORAGE_KEY,
      JSON.stringify(uiCustomization)
    );
  }, [uiCustomization]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    persistWorkspaceModes(customWorkspaceModes, window.localStorage);
  }, [customWorkspaceModes]);

  const themeConfig = useMemo(
    () => ({
      algorithm:
        activePreset.mode === 'dark' ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
      token: {
        ...resolvedAntd,
      },
      components: {
        Button: {
          borderRadius: resolvedAntd.borderRadius - 2,
          controlHeight: resolvedAntd.controlHeight,
          fontWeight: 600,
          defaultBorderColor: resolvedCssVars['--shell-border'],
          defaultHoverBorderColor: resolvedAntd.colorPrimary,
        },
        Card: {
          borderRadiusLG: resolvedAntd.borderRadius,
        },
        Drawer: {
          colorBgElevated: resolvedCssVars['--surface-2'],
        },
        Menu: {
          itemBorderRadius: Math.max(resolvedAntd.borderRadius - 2, 8),
          itemHeight: resolvedAntd.menuItemHeight || customizationTheme.antd.menuItemHeight,
          itemMarginInline: 6,
          itemMarginBlock: 4,
        },
        Input: {
          borderRadius: Math.max(resolvedAntd.borderRadius - 2, 8),
          controlHeight: resolvedAntd.controlHeight,
        },
        Select: {
          borderRadius: Math.max(resolvedAntd.borderRadius - 2, 8),
          controlHeight: resolvedAntd.controlHeight,
        },
        DatePicker: {
          borderRadius: Math.max(resolvedAntd.borderRadius - 2, 8),
          controlHeight: resolvedAntd.controlHeight,
        },
        Table: {
          borderColor: resolvedCssVars['--table-border'],
          headerBg: resolvedCssVars['--table-head-bg'],
          headerColor: resolvedCssVars['--table-header-text'],
          rowHoverBg: resolvedCssVars['--table-row-hover'],
          headerSplitColor: resolvedCssVars['--table-border'],
        },
        Tag: {
          borderRadiusSM: 999,
        },
        Switch: {
          handleBg: resolvedCssVars['--switch-handle-bg'] || resolvedCssVars['--surface-1'],
          handleShadow: resolvedCssVars['--shadow-soft'],
          handleSize: 24,
          trackHeight: 30,
          trackMinWidth: 52,
          trackPadding: 3,
        },
        Radio: {
          buttonBg: resolvedCssVars['--document-neutral-card-bg'],
          buttonCheckedBg: resolvedCssVars['--accent-soft'],
          buttonColor: resolvedCssVars['--text-main'],
          buttonCheckedColor: resolvedCssVars['--brand-strong'],
        },
      },
    }),
    [activePreset.mode, customizationTheme.antd.menuItemHeight, resolvedAntd, resolvedCssVars]
  );

  const contextValue = useMemo(
    () => ({
      presetId,
      activePreset,
      presets: designPresetList,
      activeNavigationLayout,
      navigationLayouts: navigationLayoutList,
      activeWorkspaceMode,
      workspaceModes,
      uiCustomization,
      showHelperText,
      customizationOptions: designCustomizationOptions,
      showAssistPanel,
      designScopes: {
        user: {
          presetId,
          activePreset,
          activeNavigationLayout,
          activeWorkspaceMode,
          uiCustomization,
          showAssistPanel,
        },
        tenant: {
          presetId: tenantPresetId,
          activePreset: tenantActivePreset,
          activeNavigationLayout: tenantActiveNavigationLayout,
          activeWorkspaceMode: tenantActiveWorkspaceMode,
          uiCustomization: tenantUiCustomization,
          showAssistPanel: tenantShowAssistPanel,
        },
      },
      canManageTenantDesignDefaults,
      applyWorkspaceMode,
      applyWorkspaceModeForScope,
      setPresetId,
      setPresetIdForScope,
      setUiCustomization,
      setUiCustomizationForScope,
      resetUiCustomization,
      resetUiCustomizationForScope,
      setShowAssistPanel,
      setShowAssistPanelForScope,
      saveWorkspaceMode,
      removeWorkspaceMode,
    }),
    [
      activeNavigationLayout,
      activePreset,
      activeWorkspaceMode,
      applyWorkspaceMode,
      applyWorkspaceModeForScope,
      canManageTenantDesignDefaults,
      presetId,
      removeWorkspaceMode,
      resetUiCustomization,
      resetUiCustomizationForScope,
      saveWorkspaceMode,
      setPresetId,
      setPresetIdForScope,
      setShowAssistPanel,
      setShowAssistPanelForScope,
      setUiCustomization,
      setUiCustomizationForScope,
      showAssistPanel,
      showHelperText,
      tenantActiveNavigationLayout,
      tenantActivePreset,
      tenantActiveWorkspaceMode,
      tenantPresetId,
      tenantShowAssistPanel,
      tenantUiCustomization,
      uiCustomization,
      workspaceModes,
    ]
  );

  return (
    <DesignSystemContext.Provider value={contextValue}>
      <ConfigProvider theme={themeConfig}>{children}</ConfigProvider>
    </DesignSystemContext.Provider>
  );
}
